import { useApolloClient } from '@vue/apollo-composable'

import {
  LoginCodeStatusDocument,
  useLoginCodeStatusLazyQuery,
  useRemoveLoginCodeMutation,
  useSetLoginCodeMutation,
} from '~/shared/api'
import { PIN_RECOVERY_PROMPT_KEY } from './const'
import { getAuthCookieOptions } from './cookies'
import { usePinBiometric } from './pinBiometric'

export function useLoginCode() {
  const loginCodeEnabled = useCookie<string | number | null | undefined>(
    'login_code_enabled',
    getAuthCookieOptions(60 * 60 * 24 * 365),
  )
  const apolloClient = import.meta.client ? useApolloClient().client : null
  const { mutate: setLoginCodeMutation } = useSetLoginCodeMutation()
  const { mutate: removeLoginCodeMutation } = useRemoveLoginCodeMutation()
  const { load: loadLoginCodeStatus } = useLoginCodeStatusLazyQuery()
  const {
    disable: disableBiometric,
    disableAll: disableAllBiometric,
  } = usePinBiometric()

  function syncLoginCodeStatusCache(isEnabled: boolean) {
    if (!apolloClient) {
      return
    }

    try {
      apolloClient.cache.writeQuery({
        query: LoginCodeStatusDocument,
        data: {
          __typename: 'Query',
          loginCodeStatus: isEnabled,
        },
      })
    }
    catch {
      // cache may be unavailable before first query
    }
  }

  function getLoginCodeFlagValue() {
    const value = loginCodeEnabled.value

    if (value === '1' || value === 1) {
      return '1'
    }

    if (value === '0' || value === 0) {
      return '0'
    }

    return null
  }

  function setLoginCodeEnabled(value: boolean) {
    loginCodeEnabled.value = value ? '1' : '0'
  }

  function isLoginCodeEnabled() {
    return getLoginCodeFlagValue() === '1'
  }

  function isLoginCodeStatusKnown() {
    return getLoginCodeFlagValue() !== null
  }

  function clearLoginCodeFlag() {
    loginCodeEnabled.value = null
  }

  function clearClientPinState(userId?: string) {
    clearLoginCodeFlag()

    if (userId) {
      disableBiometric(userId)
      return
    }

    disableAllBiometric()
  }

  async function fetchLoginCodeStatus(options?: { forceNetwork?: boolean }) {
    try {
      const result = options?.forceNetwork
        ? await loadLoginCodeStatus(undefined, undefined, {
          fetchPolicy: 'network-only',
        })
        : await loadLoginCodeStatus()

      if (!result) {
        return isLoginCodeEnabled()
      }

      const isEnabled = Boolean(result.loginCodeStatus)
      setLoginCodeEnabled(isEnabled)
      syncLoginCodeStatusCache(isEnabled)
      return isEnabled
    }
    catch {
      return isLoginCodeEnabled()
    }
  }

  async function setLoginCode(code: string) {
    const response = await setLoginCodeMutation({ code })

    if (!response?.data?.setLoginCode) {
      return false
    }

    setLoginCodeEnabled(true)
    syncLoginCodeStatusCache(true)
    return true
  }

  async function removeLoginCode(userId?: string) {
    const response = await removeLoginCodeMutation()

    if (!response?.data?.removeLoginCode) {
      return false
    }

    setLoginCodeEnabled(false)
    syncLoginCodeStatusCache(false)
    disableBiometric(userId)
    return true
  }

  function markPinRecoveryPrompt() {
    if (import.meta.client) {
      sessionStorage.setItem(PIN_RECOVERY_PROMPT_KEY, '1')
    }
  }

  function consumePinRecoveryPrompt() {
    if (!import.meta.client) {
      return false
    }

    const value = sessionStorage.getItem(PIN_RECOVERY_PROMPT_KEY)

    if (value) {
      sessionStorage.removeItem(PIN_RECOVERY_PROMPT_KEY)
      return true
    }

    return false
  }

  return {
    loginCodeEnabled,
    setLoginCodeEnabled,
    isLoginCodeEnabled,
    isLoginCodeStatusKnown,
    clearLoginCodeFlag,
    clearClientPinState,
    fetchLoginCodeStatus,
    setLoginCode,
    removeLoginCode,
    markPinRecoveryPrompt,
    consumePinRecoveryPrompt,
  }
}
